// RollupSummaryCard.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './RollupSummaryCard.css';

/**
 * RollupSummaryCard
 *
 * Renders one rollup summary card with optional typographic scaling.
 *
 * @param {number|string|null} [labelFontSize] - Optional explicit label font size.
 * @param {number} [labelScale=1] - Multiplier applied to the default label size.
 * @param {number|string|null} [minWidth] - Optional minimum card width.
 * @param {object|null} result - Evaluated rollup result row.
 * @param {number|string|null} [valueFontSize] - Optional explicit value font size.
 * @param {number} [valueScale=1] - Multiplier applied to the default value size.
 *
 * @returns {ReactNode} The rendered rollup summary card.
 */
const RollupSummaryCard = React.memo(({
    labelFontSize = null,
    labelScale = 1,
    minWidth = null,
    result,
    valueFontSize = null,
    valueScale = 1,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    if (!result) return null;

    const customColor = String(result?.valueColor || '').trim();
    const usesCustomColor = Boolean(customColor && customColor !== 'auto');
    const valueColor = usesCustomColor ? customColor : theme.panel;
    const resolvedLabelFontSize = labelFontSize != null
        ? labelFontSize
        : `${11 * Math.max(0.5, Number(labelScale) || 1)}px`;
    const resolvedValueFontSize = valueFontSize != null
        ? valueFontSize
        : `${24 * Math.max(0.5, Number(valueScale) || 1)}px`;
    // ----- RENDER ----- //

    return (
        <div
          className="rollup-summary-card-container"
          style={{
            background: usesCustomColor ? valueColor : theme.backgroundMuted,
            minWidth: minWidth != null ? minWidth : undefined,
          }}
        >
            {/* TITLE */}
            <div
              className="rollup-summary-card-title"
              style={{
                    color: usesCustomColor ? theme.foreground : theme.foregroundMuted,
                    fontSize: resolvedLabelFontSize,
                }}
            >
                {result?.title || 'Rollup'}
            </div>

            {/* VALUE */}
            <div
              className="rollup-summary-card-value"
              style={{
                fontSize: resolvedValueFontSize,
              }}
            >
                {result?.valueDisplay || '--'}
            </div>
        </div>
    );
});

// Dev Tools
RollupSummaryCard.displayName = 'RollupSummaryCard';

export default RollupSummaryCard;
